export const EUR_MARKET_COUNTRY_CODES = [
  'AT',
  'BE',
  'BG',
  'HR',
  'CY',
  'CZ',
  'DK',
  'EE',
  'FI',
  'FR',
  'DE',
  'GR',
  'HU',
  'IE',
  'IT',
  'LV',
  'LI',
  'LT',
  'LU',
  'MT',
  'NL',
  'NO',
  'PL',
  'PT',
  'RO',
  'SK',
  'SI',
  'ES',
  'SE',
] as const;

const EUR_MARKET_COUNTRIES = new Set<string>(EUR_MARKET_COUNTRY_CODES);

const normalizeCountryCode = (countryCode: string | null | undefined): string =>
  countryCode?.trim().toUpperCase() ?? '';

export const isEuroMarketCountry = (countryCode: string | null | undefined): boolean =>
  EUR_MARKET_COUNTRIES.has(normalizeCountryCode(countryCode));

export const resolveUserPaywall = (userLocation: string | null | undefined): 'eur' | 'usd' =>
  isEuroMarketCountry(userLocation) ? 'eur' : 'usd';

export const GEO_CURRENCY_MAP: Record<string, string> = {
  ...Object.fromEntries(EUR_MARKET_COUNTRY_CODES.map((code) => [code, 'eur'] as [string, string])),
  BR: 'brl',
  CA: 'cad',
  AU: 'aud',
  CO: 'cop',
  ZA: 'zar',
  PA: 'pan',
  GB: 'gbp',
  // MX: 'mxn',
  // AR: 'ars',
};

export type PaywallTier = string;

export const getGeoPaywallTier = (userLocation: string | null | undefined): PaywallTier | null => {
  const countryCode = normalizeCountryCode(userLocation);
  if (!countryCode) return null;

  const tier = GEO_CURRENCY_MAP[countryCode]?.toLowerCase();
  return tier || null;
};

const RESERVED_PAYWALL_KEYS = new Set(['defaultPlanIndex']);

export const getPaywallTierKeys = (
  paywallSet: Record<string, unknown> | undefined,
): readonly string[] => {
  if (!paywallSet) return [];
  return Object.keys(paywallSet).filter((key) => !RESERVED_PAYWALL_KEYS.has(key));
};

export const resolvePlansTier = (
  userLocation: string | null | undefined,
  availableTiers: readonly string[],
): PaywallTier => {
  if (!availableTiers.length) {
    return resolveUserPaywall(userLocation);
  }

  const geoTier = getGeoPaywallTier(userLocation);
  if (geoTier && availableTiers.includes(geoTier)) {
    return geoTier;
  }

  const defaultTier = resolveUserPaywall(userLocation);
  if (availableTiers.includes(defaultTier)) {
    return defaultTier;
  }

  if (availableTiers.includes('usd')) return 'usd';
  if (availableTiers.includes('eur')) return 'eur';

  return availableTiers[0];
};

export const pickPlansForUser = (
  paywallSet: Record<string, unknown> | undefined,
  userLocation: string | null | undefined,
): any[] => {
  if (!paywallSet) return [];

  const tier = resolvePlansTier(userLocation, getPaywallTierKeys(paywallSet));
  const plans = paywallSet[tier];

  if (Array.isArray(plans)) return plans;

  const usdPlans = paywallSet.usd;
  if (Array.isArray(usdPlans)) return usdPlans;

  const eurPlans = paywallSet.eur;
  if (Array.isArray(eurPlans)) return eurPlans;

  return [];
};

export const getCurrencyFractionDigits = (currencyCode: string): number => {
  try {
    const digits = new Intl.NumberFormat('en', {
      style: 'currency',
      currency: currencyCode.toUpperCase(),
    }).resolvedOptions().maximumFractionDigits;
    return typeof digits === 'number' ? digits : 2;
  } catch {
    return 2;
  }
};

export const roundToCurrency = (amount: number, currencyCode: string): number => {
  const digits = getCurrencyFractionDigits(currencyCode);
  const factor = 10 ** digits;
  return Math.round(Math.max(0, amount) * factor) / factor;
};
